import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonDropdown,
  Icon,
  SpaceBetween,
  TextFilter,
  TextFilterProps,
} from "@amzn/awsui-components-react";
import { moveColumns } from "./omsColumnOrdering";
import OMSColumnMove from "./OMSColumnMove";

function sameOrder(a: string[], b: string[]) {
  return a.length === b.length && a.every((name, index) => name === b[index]);
}

export default function OMSColumnOrder({
  names,
  reportOrder,
  onChange,
  onEmpty,
  maxHeight,
}: {
  names: string[];
  reportOrder: string[];
  onChange: (names: string[]) => void;
  onEmpty: () => void;
  maxHeight?: number;
}) {
  const instructions = useId();
  const handles = useRef(new Map<string, HTMLButtonElement>());
  const searchControl = useRef<TextFilterProps.Ref>(null);
  useEffect(() => {
    searchControl.current?.focus();
  }, []);
  const pendingFocus = useRef<string>();
  const pendingElement = useRef<HTMLElement>();
  const actionOrigin = useRef(false);
  const list = useRef<HTMLOListElement>(null);
  const scrollFrame = useRef<number>();
  const scrollSpeed = useRef(0);
  const [moving, setMoving] = useState<string>();
  const [search, setSearch] = useState("");
  const [announcement, setAnnouncement] = useState("");
  const [dragging, setDragging] = useState<string>();
  const [target, setTarget] = useState<{ name: string; after: boolean }>();
  const [grabbed, setGrabbed] = useState<{
    name: string;
    original: string[];
    expected: string[];
  }>();
  useEffect(() => {
    if (grabbed && !sameOrder(names, grabbed.expected)) setGrabbed(undefined);
    if (pendingFocus.current) {
      const element = pendingElement.current;
      const handle = handles.current.get(pendingFocus.current);
      if (element?.isConnected && !element.matches(":disabled"))
        element.focus();
      else if (handle && !handle.disabled) handle.focus();
      else searchControl.current?.focus();
      pendingFocus.current = undefined;
      pendingElement.current = undefined;
    }
    if (moving && !names.includes(moving)) setMoving(undefined);
  }, [names, grabbed, moving]);
  const stopScroll = useCallback(() => {
    if (scrollFrame.current !== undefined)
      window.cancelAnimationFrame(scrollFrame.current);
    scrollFrame.current = undefined;
    scrollSpeed.current = 0;
  }, []);
  useEffect(() => stopScroll, [dragging, stopScroll]);
  const scroll = useCallback(
    (event: React.DragEvent<HTMLOListElement>) => {
      if (!dragging || !list.current) return;
      event.preventDefault();
      const rect = list.current.getBoundingClientRect();
      const distance = Math.min(40, rect.height / 4);
      let speed = 0;
      if (event.clientY < rect.top + distance)
        speed = -Math.ceil((rect.top + distance - event.clientY) / 4);
      else if (event.clientY > rect.bottom - distance)
        speed = Math.ceil((event.clientY - rect.bottom + distance) / 4);
      scrollSpeed.current = Math.max(-10, Math.min(10, speed));
      if (!scrollSpeed.current) {
        stopScroll();
        return;
      }
      if (scrollFrame.current !== undefined) return;
      function tick() {
        if (list.current) list.current.scrollTop += scrollSpeed.current;
        scrollFrame.current = window.requestAnimationFrame(tick);
      }
      scrollFrame.current = window.requestAnimationFrame(tick);
    },
    [dragging, stopScroll]
  );
  const items = names.filter((name) =>
    name.toLocaleLowerCase().includes(search.toLocaleLowerCase())
  );
  function move(name: string, position: number) {
    const next = moveColumns(names, [name], position);
    if (sameOrder(next, names)) return;
    const focused = document.activeElement;
    if (
      focused instanceof HTMLElement &&
      focused.closest("[data-column]")?.getAttribute("data-column") === name
    ) {
      pendingFocus.current = name;
      pendingElement.current = focused;
    }
    if (grabbed?.name === name) setGrabbed({ ...grabbed, expected: next });
    onChange(next);
    setAnnouncement(`Moved ${name} to position ${position}.`);
  }
  function toggleGrab(name: string) {
    if (grabbed?.name === name) {
      setGrabbed(undefined);
      setAnnouncement(`Moved ${name} to position ${names.indexOf(name) + 1}.`);
    } else {
      setGrabbed({ name, original: [...names], expected: [...names] });
      setAnnouncement(`Moving ${name}. Use Up or Down, then Enter to finish.`);
    }
  }
  function keyDown(
    event: React.KeyboardEvent<HTMLButtonElement>,
    name: string
  ) {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      toggleGrab(name);
    } else if (grabbed?.name === name) {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        pendingFocus.current = name;
        pendingElement.current = handles.current.get(name);
        onChange(grabbed.original);
        setGrabbed(undefined);
        setAnnouncement("Column move cancelled.");
      } else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        event.stopPropagation();
        move(name, names.indexOf(name) + (event.key === "ArrowUp" ? 0 : 2));
      }
    }
  }
  function remove(name: string) {
    const index = names.indexOf(name);
    const next = names.filter((item) => item !== name);
    pendingFocus.current = next[Math.min(index, next.length - 1)];
    onChange(next);
    setGrabbed(undefined);
    setAnnouncement(`Removed ${name} from the export.`);
    if (!next.length) onEmpty();
  }
  function drop(event: React.DragEvent<HTMLLIElement>, name: string) {
    event.preventDefault();
    if (dragging && target?.name === name && dragging !== name) {
      const remaining = names.filter((item) => item !== dragging);
      move(dragging, remaining.indexOf(name) + (target.after ? 2 : 1));
    }
    setDragging(undefined);
    setTarget(undefined);
    stopScroll();
  }
  function startDrag(event: React.DragEvent<HTMLLIElement>, name: string) {
    const origin = event.target;
    if (
      search ||
      actionOrigin.current ||
      (origin instanceof Element &&
        origin.closest(".oms-columns__order-actions"))
    ) {
      event.preventDefault();
      return;
    }
    setGrabbed(undefined);
    setDragging(name);
    const transfer = event.dataTransfer;
    transfer.effectAllowed = "move";
    transfer.setData("text/plain", name);
  }
  return (
    <div className="oms-columns__order-editor" style={{ maxHeight }}>
      <div className="oms-columns__order-toolbar">
        <SpaceBetween size="s">
          <Box color="text-body-secondary">
            Drag rows or use Move to change the CSV order.
          </Box>
          <div className="oms-columns__order-controls">
            <TextFilter
              ref={searchControl}
              filteringText={search}
              filteringPlaceholder="Find selected columns"
              filteringAriaLabel="Find selected columns"
              filteringClearAriaLabel="Clear selected column search"
              onChange={({ detail }) => {
                setSearch(detail.filteringText);
                setGrabbed(undefined);
              }}
            />
            <Button
              onClick={() => {
                onChange(
                  [...names].sort(
                    (a, b) => reportOrder.indexOf(a) - reportOrder.indexOf(b)
                  )
                );
                setAnnouncement("Original report column order restored.");
              }}
            >
              Restore report order
            </Button>
          </div>
          {search && (
            <Box color="text-body-secondary">
              Clear search to drag. Move uses the full CSV order.
            </Box>
          )}
          {moving && (
            <OMSColumnMove
              key={moving}
              name={moving}
              names={names}
              onMove={(position) => {
                pendingFocus.current = moving;
                pendingElement.current = handles.current.get(moving);
                move(moving, position);
                setMoving(undefined);
              }}
              onCancel={() => {
                pendingFocus.current = moving;
                pendingElement.current = handles.current.get(moving);
                setMoving(undefined);
              }}
            />
          )}
        </SpaceBetween>
      </div>
      <span id={instructions} className="oms-columns__keyboard-help">
        Press Space or Enter to start moving a column, Up or Down to move, Enter
        to finish, or Escape to cancel.
      </span>
      {/* Keyboard users can scroll a large selection without entering every row. */}
      {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
      <ol
        ref={list}
        className="oms-columns__order"
        aria-label="CSV column order"
        tabIndex={0}
        onDragOver={scroll}
        onDragLeave={(event) => {
          if (
            !(event.relatedTarget instanceof Node) ||
            !event.currentTarget.contains(event.relatedTarget)
          )
            stopScroll();
        }}
      >
        {items.map((name) => {
          const index = names.indexOf(name);
          const dropClass =
            target?.name === name
              ? ` oms-columns__drop-${target.after ? "after" : "before"}`
              : "";
          return (
            <li
              key={name}
              aria-label={name}
              data-column={name}
              className={`oms-columns__order-row${dropClass}${
                dragging === name ? " oms-columns__dragging" : ""
              }`}
              draggable={!search}
              onPointerDownCapture={(event) => {
                actionOrigin.current =
                  event.target instanceof Element &&
                  Boolean(event.target.closest(".oms-columns__order-actions"));
              }}
              onDragStart={(event) => startDrag(event, name)}
              onDragEnd={() => {
                setDragging(undefined);
                setTarget(undefined);
                actionOrigin.current = false;
                stopScroll();
              }}
              onDragOver={(event) => {
                if (!dragging || search) return;
                event.preventDefault();
                const transfer = event.dataTransfer;
                transfer.dropEffect = "move";
                const rect = event.currentTarget.getBoundingClientRect();
                setTarget(
                  dragging === name
                    ? undefined
                    : {
                        name,
                        after: event.clientY > rect.top + rect.height / 2,
                      }
                );
              }}
              onDrop={(event) => drop(event, name)}
            >
              <button
                type="button"
                ref={(element) => {
                  if (element) handles.current.set(name, element);
                  else handles.current.delete(name);
                }}
                className="oms-columns__drag-handle"
                aria-label={`Reorder ${name}`}
                title={`Reorder ${name}`}
                aria-describedby={instructions}
                aria-pressed={grabbed?.name === name}
                disabled={Boolean(search)}
                onClick={() => toggleGrab(name)}
                onKeyDown={(event) => keyDown(event, name)}
                onBlur={() => {
                  if (pendingFocus.current !== name) setGrabbed(undefined);
                }}
              >
                <Icon name="drag-indicator" />
              </button>
              <span className="oms-columns__order-name">
                <span
                  className="oms-columns__position"
                  title={`Position ${index + 1} of ${names.length}`}
                >
                  {index + 1}
                </span>
                {name}
              </span>
              <div className="oms-columns__order-actions">
                <SpaceBetween direction="horizontal" size="xxs">
                  <ButtonDropdown
                    expandToViewport
                    ariaLabel={`Move ${name}`}
                    disabled={names.length === 1}
                    items={[
                      { id: "up", text: "Move up", disabled: index === 0 },
                      {
                        id: "down",
                        text: "Move down",
                        disabled: index === names.length - 1,
                      },
                      { id: "location", text: "Choose location" },
                    ]}
                    onItemClick={({ detail }) => {
                      if (detail.id === "up") move(name, index);
                      else if (detail.id === "down") move(name, index + 2);
                      else setMoving(name);
                    }}
                  >
                    Move
                  </ButtonDropdown>
                  <Button
                    iconName="remove"
                    ariaLabel={`Remove column ${name}`}
                    onClick={() => remove(name)}
                  />
                </SpaceBetween>
              </div>
            </li>
          );
        })}
      </ol>
      {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
      {!items.length && (
        <Box textAlign="center">No selected columns match your search.</Box>
      )}
      {announcement && (
        <div
          role="status"
          aria-live="polite"
          className="oms-columns__keyboard-help"
        >
          <Box color="text-body-secondary">{announcement}</Box>
        </div>
      )}
    </div>
  );
}
